import { useState, useEffect, useCallback, useRef } from "react"
import { apiClient, erpApi, type DealName, type AccAssignment, type UnitOfMeasure } from "@/lib/api"

export interface AnalyticPartOption {
  value: string
  label: string
  apx_part?: string
  apx_id?: number
  value_fk?: number
}

export interface AnalyticPartFieldDef {
  field_id: number
  field_name: string
  label: string
  row_num: number
  lookup_nomen_type_fk: number
  options: AnalyticPartOption[]
  all_options: AnalyticPartOption[]
}

export interface AnalyticPartDefinition {
  account_id: number
  account_code: string
  account_name: string
  analytic_part_id: number
  acc_obj_id: number
  directions: string[]
  lookup_nomen_type_fk: number
  options: AnalyticPartOption[]
  fields?: AnalyticPartFieldDef[]
  combinations: Array<{
    apx_id: number
    levels: Array<{ row_num: number; code: string; name: string }>
  }>
}

interface AnalyticPartResponse {
  acc_assignment_id: number
  taken_position?: number | null
  analytic_parts: AnalyticPartDefinition[]
}

export function useErpDealNames(clientId?: string, isIncome = false) {
  const [dealNames, setDealNames] = useState<DealName[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const actionType = isIncome ? 102 : 103

  useEffect(() => {
    if (!clientId) return

    const fetch = async () => {
      setIsLoading(true)
      try {
        const data = await erpApi.getDealNames(clientId, { actionType })
        setDealNames(data.deal_names || [])
      } catch (error) {
        console.error("Error fetching deal names:", error)
        setDealNames([])
      } finally {
        setIsLoading(false)
      }
    }

    fetch()
  }, [clientId, actionType])

  return { dealNames, isLoading }
}

export function useErpUnitsOfMeasure(clientId?: string) {
  const [units, setUnits] = useState<UnitOfMeasure[]>([])
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (!clientId) return

    const fetchUnits = async () => {
      setIsLoading(true)
      try {
        const data = await erpApi.getUnitsOfMeasure(clientId)
        setUnits(data.units_of_measure || [])
      } catch (error) {
        console.error("Error fetching units of measure:", error)
        setUnits([])
      } finally {
        setIsLoading(false)
      }
    }

    fetchUnits()
  }, [clientId])

  return { units, isLoading }
}

export function useErpAccAssignments(clientId?: string, isIncome = false) {
  const [accAssignments, setAccAssignments] = useState<AccAssignment[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const actionType = isIncome ? 102 : 103

  useEffect(() => {
    if (!clientId) return

    const fetch = async () => {
      setIsLoading(true)
      try {
        const data = await erpApi.getAccAssignments(clientId, actionType)
        setAccAssignments(data.acc_assignments || [])
      } catch (error) {
        console.error("Error fetching account assignments:", error)
        setAccAssignments([])
      } finally {
        setIsLoading(false)
      }
    }

    fetch()
  }, [clientId, actionType])

  return { accAssignments, isLoading }
}

export function useErpAnalyticParts(clientId?: string, accAssignmentId?: number, isIncome = false) {
  const [definitions, setDefinitions] = useState<AnalyticPartDefinition[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const actionType = isIncome ? 102 : 103

  const load = useCallback(async (id?: number) => {
    if (!clientId || !id) {
      setDefinitions([])
      return
    }

    setIsLoading(true)
    try {
      const response = await apiClient.get<AnalyticPartResponse>(
        `/clients/${clientId}/acc-assignment/${id}/analytic-parts?action_type=${actionType}`
      )
      setDefinitions(response?.analytic_parts ?? [])
    } catch (error) {
      console.error("Error loading analytic parts:", error)
      setDefinitions([])
    } finally {
      setIsLoading(false)
    }
  }, [clientId, actionType])

  useEffect(() => {
    load(accAssignmentId)
  }, [load, accAssignmentId])

  return { definitions, isLoading }
}

/**
 * Shared cache for analytic parts across all line item rows.
 * Fetches each unique accAssignmentId only once and provides a lookup.
 * Rows call `getOrFetch(accAssignmentId)` to request data — if already
 * cached or in-flight, no duplicate request is made.
 */
export interface AnalyticPartsCache {
  get: (accAssignmentId: number | undefined) => AnalyticPartDefinition[]
  isLoading: (accAssignmentId: number | undefined) => boolean
  getOrFetch: (accAssignmentId: number | undefined) => void
}

export function useErpAnalyticPartsCache(clientId?: string, isIncome = false): AnalyticPartsCache {
  const actionType = isIncome ? 102 : 103
  // cache: accAssignmentId → { definitions, loading }
  const [cache, setCache] = useState<Record<number, { definitions: AnalyticPartDefinition[]; loading: boolean }>>({})
  // Track in-flight requests to avoid duplicates
  const inflightRef = useRef<Set<number>>(new Set())

  // Reset cache when clientId or actionType changes
  useEffect(() => {
    setCache({})
    inflightRef.current.clear()
  }, [clientId, actionType])

  const getOrFetch = useCallback((accAssignmentId: number | undefined) => {
    if (!accAssignmentId || !clientId) return
    // Already cached or in-flight
    if (cache[accAssignmentId] || inflightRef.current.has(accAssignmentId)) return

    inflightRef.current.add(accAssignmentId)
    setCache((prev) => ({ ...prev, [accAssignmentId]: { definitions: [], loading: true } }))

    apiClient.get<AnalyticPartResponse>(
      `/clients/${clientId}/acc-assignment/${accAssignmentId}/analytic-parts?action_type=${actionType}`
    ).then((response) => {
      setCache((prev) => ({
        ...prev,
        [accAssignmentId]: { definitions: response?.analytic_parts ?? [], loading: false }
      }))
    }).catch((error) => {
      console.error("Error loading analytic parts:", error)
      setCache((prev) => ({
        ...prev,
        [accAssignmentId]: { definitions: [], loading: false }
      }))
    }).finally(() => {
      inflightRef.current.delete(accAssignmentId)
    })
  }, [clientId, actionType, cache])

  const get = useCallback((accAssignmentId: number | undefined): AnalyticPartDefinition[] => {
    if (!accAssignmentId) return []
    return cache[accAssignmentId]?.definitions ?? []
  }, [cache])

  const isLoading = useCallback((accAssignmentId: number | undefined): boolean => {
    if (!accAssignmentId) return false
    return cache[accAssignmentId]?.loading ?? false
  }, [cache])

  return { get, isLoading, getOrFetch }
}
